import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Role, RoleAdapter } from '../models/role';
import { RolesService } from '../services/roles.service';
import { Observable, finalize, map } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AvatarComponent } from '../_utils/components/avatar/avatar.component';
import { ToastService } from '../services/toast.service';

@Component({
  selector: 'app-role-list',
  standalone: true,
  imports: [CommonModule, ConfirmDialogComponent, FormsModule, RouterLink, TranslatePipe, AvatarComponent],
  templateUrl: './role-list.component.html',
  styleUrl: './role-list.component.scss',
})
export class RoleListComponent {
  roles!: Role[];
  roleToDelete!: Role;
  roleToEdit: Role | null = null;
  showConfirmDialog: boolean = false;
  ShowForm: boolean = false;
  isLoading: boolean = false;

  private rolesObservable!: Observable<Role[]>;

  constructor(
    private rolesService: RolesService,
    private roleAdapter: RoleAdapter,
    private toastService: ToastService
  ) {}

  /**
   * Lifecycle hook that is called when the component is initialized.
   * Fetches all roles from the server and stores them in the component's
   * `roles` property.
   */
  ngOnInit(): void {
    this.fetchRoles();
  }

  /**
   * Retrieves all roles from the server and stores them in the component's
   * `roles` property. The roles are adapted to the `Role` model using the
   * `RoleAdapter` before being stored.
   *
   * @remarks
   * This method is called once when the component is initialized.
   */

  private fetchRoles(): void {
    this.isLoading = true;
    this.rolesObservable = this.rolesService
      .getRoles()
      .pipe(
        map((data: any[]) => data.map((item) => this.roleAdapter.adapt(item))),
        finalize(() => this.isLoading = false)
      );

    this.rolesObservable.subscribe((res: Role[]) => {
      this.roles = res;
    });
  }

  confirmDelete(role: Role): void {
    this.roleToDelete = role;
    this.showConfirmDialog = true;
  }

  handleConfirm(result: boolean): void {
    if (result) {
      this.deleteRole(this.roleToDelete);
    }
    this.showConfirmDialog = false;
  }

  editRole(role: Role): void {
    this.roleToEdit = { ...role };
    this.ShowForm = true;
  }

  cancelEdit(): void {
    this.roleToEdit = null;
    this.ShowForm = false;
  }

  addNewRole(): void {
    this.roleToEdit = new Role(0, '', '', new Date()  , new Date);
    this.ShowForm = true;
  }

  // Méthode pour soumettre le formulaire
  onSubmit(): void {
    if (this.roleToEdit) {
      let roleObservable;

      if (this.roleToEdit.id) {
        // Mise à jour d'un rôle existant
        roleObservable = this.rolesService.updateRole(this.roleToEdit);
      } else {
        // Ajout d'un nouveau rôle
        roleObservable = this.rolesService.addRole(this.roleToEdit);
      }

      roleObservable.subscribe({
        next: () => {
          // Recharger la liste entière de rôles pour la synchroniser avec le serveur
          this.fetchRoles(); 
          
          // Afficher un message de succès différent selon l'action
          if (this.roleToEdit!.id) {
            this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
          } else {
            this.toastService.showSuccess('COMMON.ADD_SUCCESS');
          }

          // Actions finales à effectuer dans tous les cas de succès
          this.roleToEdit = null;
          this.ShowForm = false;

        },
        error: (err) => {
          console.error('Erreur lors de l\'enregistrement du rôle :', err);
          this.toastService.showError('COMMON.SAVE_ERROR');
        },
      });
    }
  }

  private deleteRole(role: Role): void {
  this.rolesService.delete_role(role.id).subscribe({
    next: () => {
      this.roles = this.roles.filter((r) => r.id !== role.id);
      this.toastService.showSuccess('COMMON.DELETE_SUCCESS');
    },
    error: (err) => {
      this.toastService.showError('COMMON.DELETE_ERROR');
    }
  });
}

  trackById(index: number, role: Role): number {
    return role.id;
  }
}
